import { useEffect, useMemo, useState, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, message, Upload, UploadProps, Col, Row, Modal, Typography, Collapse } from 'antd';
import { UploadFile } from 'antd/es/upload/interface';
import { BoldLabelFormItem } from 'components/form/BoldLabelFormItem';
import { MessageType } from 'models/MessageType';
import { hasUploadedFile } from 'components/form';
import { RcFile } from 'antd/es/upload';
import useFormInstance from 'antd/es/form/hooks/useFormInstance';
import uploudlistIcon from 'assets/images/support/uploudlist.svg';
import image from 'assets/images/Image file.svg';
import downfile from 'assets/images/MakeClaim/downfile.svg';
import { useGetLifeDoc } from 'all-in-one-shared-api';
import { useKeycloakReady } from 'hooks/useKeycloakReady';
import { isLocaleEnglish } from 'utils';
import PdfFileIcon from 'assets/images/PDFfileicon.svg';

const { Text } = Typography;
const { Panel } = Collapse;

const TEN_MB = 10 * 1024 * 1024;
const MAX_PER_FILE_SIZE = TEN_MB;
const MAX_SIZE = TEN_MB * 5;
const MAX_NUMBER_OF_ATTACHMENTS = 12;
type DocumentsCardType = {
  setInvalid: (invalid: boolean) => void;
};

const DocumentUploadField = ({
  name,
  label,
  setInvalid,
  onChange,
  maxSize,
  extra,
  exceedCount,
}: {
  name: string;
  label: string;
  setInvalid?: (invalid: boolean) => void;
  onChange?: (files: UploadFile[]) => void;
  maxSize: number;
  extra?: ReactNode;
  exceedCount: boolean;
}) => {
  const { t } = useTranslation();
  const form = useFormInstance();

  const [exceedSize, setExceedSize] = useState(false);
  const [attachments, setAttachments] = useState<UploadFile[]>(() => {
    const initial = form.getFieldValue(name);
    return Array.isArray(initial) ? initial : [];
  });

  useEffect(() => {
    setInvalid?.(exceedSize || exceedCount);
  }, [exceedSize, exceedCount, setInvalid]);

  const attachmentUploadProps: UploadProps = useMemo(
    () => ({
      fileList: attachments,
      listType: 'picture',
      multiple: false,
      accept: 'image/*,application/pdf',
      onChange: ({ fileList }) => {
        const totalSize = fileList.map((a) => a.size || 0).reduce((acc, cur) => acc + cur, 0);
        setExceedSize(totalSize > maxSize);
        setAttachments(fileList);
        form.setFieldsValue({ [name]: fileList });
        onChange?.(fileList);
      },
      onRemove: () => {
        return new Promise((resolve) => {
          Modal.confirm({
            title: '',
            icon: '',
            width: 510,
            className: 'Modalconfirm',
            centered: true,
            okText: `${t('life.enquiry.Cancel')}`,
            cancelText: `${t('life.enquiry.delete')}`,
            content: `${t('life.enquiry.sureToDelete')}`,
            onOk: () => {
              return false;
            },
            onCancel: () => {
              return resolve(true);
            },
          });
        });
      },

      beforeUpload: (f) => {
        if (hasUploadedFile(attachments as RcFile[], f)) {
          message[MessageType.ERROR](t('common.error.notAllowUploadSameDocument'));
          return Upload.LIST_IGNORE;
        }

        if (f.size > MAX_PER_FILE_SIZE) {
          message[MessageType.ERROR](t('common.error.sizeExceeds', { amount: 10 }));
          return Upload.LIST_IGNORE;
        }
        // 检查文件类型
        if (f.type.indexOf('image/') !== 0 && f.type !== 'application/pdf') {
          message[MessageType.ERROR](t('common.error.notPdfOrImages'));
          return Upload.LIST_IGNORE;
        }
        return false;
      },
      onPreview: (file) => {
        if (file.originFileObj) {
          const url = URL.createObjectURL(file.originFileObj as Blob);
          window.open(url, '_blank');
        }
      },
    }),
    [attachments, t, form, name, maxSize, onChange],
  );

  return (
    <Col xs={24} md={24}>
      <BoldLabelFormItem
        name={name}
        label={
          <span style={{ color: '#752127', fontWeight: 'normal', fontSize: '17px', fontFamily: 'Roboto400' }}>
            {label}
          </span>
        }
        className={`${extra ? 'has-extra' : ''}`}
      >
        {extra}
        <Upload
          className="customUpload"
          name={name}
          {...attachmentUploadProps}
          iconRender={() => <img src={image} width={24} height={24} alt="upload list icon" />}
        >
          <div className="buttoncontent">
            <Button className="button" disabled={exceedCount}>
              {t('enquiry.upload')}
            </Button>
          </div>
          <div
            style={{
              color: '#B0B2B3',
              fontWeight: 'normal',
              fontSize: '17px',
              fontFamily: 'Roboto400',
              marginTop: '20px',
            }}
          >
            <img src={uploudlistIcon} style={{ margin: '0  10px 5px 0', width: '24px', height: '24px' }} />
            {t('life.enquiry.uploadedAttr')}
          </div>
        </Upload>
      </BoldLabelFormItem>
      {exceedSize && <Text type="danger">{t('common.error.sizeExceeds', { amount: 10 })}</Text>}
    </Col>
  );
};

export const DocumentsCardForCI = ({ setInvalid }: DocumentsCardType) => {
  const { t, i18n } = useTranslation();
  const form = useFormInstance();
  const { token } = useKeycloakReady();

  const [claimFormPartIIFiles, setClaimFormPartIIFiles] = useState<UploadFile[]>([]);
  const [identityProofFiles, setIdentityProofFiles] = useState<UploadFile[]>([]);
  const [labReportsFiles, setLabReportsFiles] = useState<UploadFile[]>([]);
  const [otherDocumentsFiles, setOtherDocumentsFiles] = useState<UploadFile[]>([]);
  // const GetLifeDocFetchData: ;
  //定义方法=========================================================
  const handleClaimFormPartIIChange = (files: UploadFile[]) => setClaimFormPartIIFiles(files);
  const handleIdentityProofChange = (files: UploadFile[]) => setIdentityProofFiles(files);
  const handleLabReportsChange = (files: UploadFile[]) => setLabReportsFiles(files);
  const handleOtherDocumentsChange = (files: UploadFile[]) => setOtherDocumentsFiles(files);
  const { shouldFetch: GetLifeDocFetch, data: GetLifeDocFetchData } = useGetLifeDoc('useGetLifeDoc', token || '');
  useEffect(() => {
    const claimFormPartII = form.getFieldValue('criticalIllnessClaimFormAttachments');
    const identityProof = form.getFieldValue('proofOfIdentityAttachments');
    const labReports = form.getFieldValue('laboratoryAndPathologicalAttachments');
    const otherDocuments = form.getFieldValue('otherAttachments');

    setClaimFormPartIIFiles(Array.isArray(claimFormPartII) ? claimFormPartII : []);
    setIdentityProofFiles(Array.isArray(identityProof) ? identityProof : []);
    setLabReportsFiles(Array.isArray(labReports) ? labReports : []);
    setOtherDocumentsFiles(Array.isArray(otherDocuments) ? otherDocuments : []);
  }, [form]);

  const totalFiles = useMemo(() => {
    return (
      claimFormPartIIFiles.length + identityProofFiles.length + labReportsFiles.length + otherDocumentsFiles.length
    );
  }, [claimFormPartIIFiles, identityProofFiles, labReportsFiles, otherDocumentsFiles]);

  const totalSize = useMemo(() => {
    const getSize = (files: UploadFile[]) => files.reduce((acc, f) => acc + (f.size || 0), 0);
    return (
      getSize(claimFormPartIIFiles) +
      getSize(identityProofFiles) +
      getSize(labReportsFiles) +
      getSize(otherDocumentsFiles)
    );
  }, [claimFormPartIIFiles, identityProofFiles, labReportsFiles, otherDocumentsFiles]);

  useEffect(() => {
    setInvalid(totalFiles > MAX_NUMBER_OF_ATTACHMENTS || totalSize > MAX_SIZE);
  }, [totalFiles, totalSize, setInvalid]);

  useEffect(() => {
    GetLifeDocFetch(true);
  }, [GetLifeDocFetch]);

  return (
    <Row justify="center" gutter={[16, { xs: 8, sm: 8, lg: 24 }]}>
      <Col xs={24} md={24} style={{ marginBottom: '10px' }}>
        <Typography.Text className="headertext">{t('life.submitClaims.uploadTips')}</Typography.Text>
      </Col>
      <Col xs={24} md={24}>
        <div className="speakheader" style={{ paddingLeft: 0 }}>
          {t('life.enquiry.Supplementary')}
        </div>
      </Col>
      <Row justify="center" gutter={[16, { xs: 8, sm: 8, lg: 12 }]}>
        <Col xs={24} md={24} style={{ marginBottom: '20px' }}>
          <div
            style={{
              border: '1px solid #e8e8e8',
              borderRadius: '4px',
              padding: '16px',
              width: '100%',
              overflow: 'hidden',
            }}
          >
            <DocumentUploadField
              name="criticalIllnessClaimFormAttachments"
              label={t('life.submitClaims.claimFormPartII')}
              onChange={handleClaimFormPartIIChange}
              maxSize={MAX_SIZE - (totalSize - claimFormPartIIFiles.reduce((acc, f) => acc + (f.size || 0), 0))}
              exceedCount={totalFiles >= MAX_NUMBER_OF_ATTACHMENTS}
              extra={
                <div style={{ width: '100%', maxWidth: '100%' }}>
                  <Collapse
                    accordion
                    className="Collapse downloadHeaderCollapse"
                    expandIconPosition="end"
                    style={{ width: '100%', marginTop: 0 }}
                  >
                    <Panel
                      header={
                        <div className="Downloadheader Downloadheader--no-border-top" style={{ maxWidth: '100%' }}>
                          <div>
                            <img src={downfile} style={{ margin: '0  10px 5px 0', width: '24px', height: '24px' }} />
                            {t('life.MakeClaim.DownloadAClaimForm')}
                          </div>
                        </div>
                      }
                      key="1"
                    >
                      {GetLifeDocFetchData?.criticalIllness
                        ?.filter((item) => item.docOrder != '10')
                        ?.map((item, index) => {
                          return (
                            <div key={index}>
                              <a
                                key={index}
                                href={isLocaleEnglish(i18n.language) ? item.docUrlEn || '' : item.docUrlTc || ''}
                                target="_blank"
                                rel="noreferrer"
                              >
                                <div>
                                  <img
                                    src={PdfFileIcon}
                                    style={{ margin: '0  10px 5px 0', width: '24px', height: '24px' }}
                                  />
                                  <span style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                                    {isLocaleEnglish(i18n.language) ? item.docDescEn || '' : item.docDescTc || ''}
                                  </span>
                                </div>
                              </a>
                            </div>
                          );
                        })}
                    </Panel>
                  </Collapse>
                </div>
              }
            />
          </div>
        </Col>

        <Col xs={24} md={24} style={{ marginBottom: '20px' }}>
          <div
            style={{
              border: '1px solid #e8e8e8',
              borderRadius: '4px',
              padding: '16px',
              width: '100%',
              overflow: 'hidden',
            }}
          >
            <DocumentUploadField
              name="proofOfIdentityAttachments"
              label={t('life.submitClaims.identityProof')}
              onChange={handleIdentityProofChange}
              maxSize={MAX_SIZE - (totalSize - identityProofFiles.reduce((acc, f) => acc + (f.size || 0), 0))}
              exceedCount={totalFiles >= MAX_NUMBER_OF_ATTACHMENTS}
            />
          </div>
        </Col>

        <Col xs={24} md={24} style={{ marginBottom: '20px' }}>
          <div
            style={{
              border: '1px solid #e8e8e8',
              borderRadius: '4px',
              padding: '16px',
              width: '100%',
              overflow: 'hidden',
            }}
          >
            <DocumentUploadField
              name="laboratoryAndPathologicalAttachments"
              label={t('life.submitClaims.labReports')}
              onChange={handleLabReportsChange}
              maxSize={MAX_SIZE - (totalSize - labReportsFiles.reduce((acc, f) => acc + (f.size || 0), 0))}
              exceedCount={totalFiles >= MAX_NUMBER_OF_ATTACHMENTS}
            />
          </div>
        </Col>

        <Col xs={24} md={24} style={{ marginBottom: '20px' }}>
          <div
            style={{
              border: '1px solid #e8e8e8',
              borderRadius: '4px',
              padding: '16px',
              width: '100%',
              overflow: 'hidden',
            }}
          >
            <DocumentUploadField
              name="otherAttachments"
              label={t('life.submitClaims.otherDocuments')}
              onChange={handleOtherDocumentsChange}
              maxSize={MAX_SIZE - (totalSize - otherDocumentsFiles.reduce((acc, f) => acc + (f.size || 0), 0))}
              exceedCount={totalFiles >= MAX_NUMBER_OF_ATTACHMENTS}
              extra={
                <div className="Collapse" style={{ width: '100%', maxWidth: '100%', paddingLeft: 0, marginTop: 0 }}>
                  {GetLifeDocFetchData?.lifeCIOtherDoc?.map((item, index) => {
                    return (
                      <div key={index} style={{ padding: '0 16px' }}>
                        <a
                          key={index}
                          href={isLocaleEnglish(i18n.language) ? item.docUrlEn || '' : item.docUrlTc || ''}
                          target="_blank"
                          rel="noreferrer"
                        >
                          <div>
                            <img src={PdfFileIcon} style={{ margin: '0  10px 5px 0', width: '24px', height: '24px' }} />
                            <span style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                              {isLocaleEnglish(i18n.language) ? item.docDescEn || '' : item.docDescTc || ''}
                            </span>
                          </div>
                        </a>
                      </div>
                    );
                  })}
                </div>
              }
            />
          </div>
        </Col>
        {totalFiles >= MAX_NUMBER_OF_ATTACHMENTS && <Text type="danger">{t('enquiry.error.noOfFilesExceeds')}</Text>}
      </Row>
      <div
        style={{
          color: '#6f7072',
          fontWeight: 'normal',
          fontSize: '17px',
          fontFamily: 'Roboto400',
          whiteSpace: 'pre-line',
          width: '100%',
        }}
      >
        {t('life.submitClaims.uploadDescForCI')}
      </div>
    </Row>
  );
};
